import React from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatNumber(value) {
  const numberValue = Number(value || 0);

  if (numberValue >= 1000000) {
    return thousandsSeparator((numberValue / 1000000).toFixed(1)) + "M";
  }

  if (numberValue >= 1000) {
    return thousandsSeparator((numberValue / 1000).toFixed(1)) + "K";
  }

  return thousandsSeparator(numberValue.toFixed(0));
}

function formatChange(change) {
  if (change === null || change === undefined) return null;

  const numberValue = Number(change);

  if (Number.isNaN(numberValue)) return null;
  if (numberValue > 0) return `+${numberValue.toFixed(1)}%`;
  if (numberValue < 0) return `${numberValue.toFixed(1)}%`;

  return "0.0%";
}

function PromotionKpiCard({
  title,
  value,
  badge,
  caption,
  color = '#1a237e',
  isPercent = false,
  isMoney = false,
  change,
  changeLabel = "vs previous period",
}) {
  const numberValue = Number(value || 0);

  let displayValue = formatNumber(numberValue);

  if (isPercent) {
    displayValue = `${numberValue.toFixed(1)}%`;
  }

  if (isMoney) {
    displayValue = formatNumber(numberValue);
  }

  const formattedChange = formatChange(change);

  let changeColor = '#64748b';
  let changeBg = 'rgba(100, 116, 139, 0.10)';

  if (Number(change) > 0) {
    changeColor = '#047857';
    changeBg = 'rgba(4, 120, 87, 0.10)';
  }

  if (Number(change) < 0) {
    changeColor = '#b91c1c';
    changeBg = 'rgba(185, 28, 28, 0.10)';
  }

  return (
    <Card
      sx={{
        overflow: 'hidden',
        position: 'relative',
        borderRadius: { xs: '14px', sm: '16px' },
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
        background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
        border: '1px solid rgba(15, 23, 42, 0.06)',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: color,
        },
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          boxSizing: 'border-box',
          p: {
            xs: 1.35,
            sm: 1.75,
            md: 2,
          },
          '&:last-child': {
            pb: {
              xs: 1.35,
              sm: 1.75,
              md: 2,
            },
          },
        }}
      >
        <Stack
          sx={{
            height: '100%',
            minHeight: {
              xs: 96,
              sm: 118,
            },
          }}
          justifyContent="space-between"
          spacing={1.1}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                lineHeight: 1.2,
                color: color,
                fontWeight: 800,
                letterSpacing: '.015em',
                fontSize: {
                  xs: '0.68rem',
                  sm: '0.8rem',
                  md: '0.84rem',
                },
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                mt: 0.6,
                fontWeight: 800,
                lineHeight: 1,
                color: '#111827',
                fontSize: {
                  xs: '1.45rem',
                  sm: '2rem',
                  md: '2.35rem',
                },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayValue}
            </Typography>
          </Box>

          <Stack
            spacing={0.65}
            sx={{
              minWidth: 0,
            }}
          >
            <Stack
              alignItems="center"
              direction="row"
              spacing={0.65}
              sx={{ minWidth: 0 }}
            >
              {badge !== undefined && badge !== null && badge !== "" && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.35,
                    px: 0.7,
                    py: 0.25,
                    borderRadius: 999,
                    bgcolor: 'rgba(26, 35, 126, 0.08)',
                    flexShrink: 0,
                    maxWidth: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      color: color,
                      fontWeight: 800,
                      fontSize: {
                        xs: '0.72rem',
                        sm: '0.88rem',
                      },
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {badge}
                  </Typography>
                </Box>
              )}

              {caption && (
                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: {
                      xs: '0.68rem',
                      sm: '0.82rem',
                    },
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}
                >
                  {caption}
                </Typography>
              )}
            </Stack>

            {formattedChange && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.6}
                sx={{ minWidth: 0 }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 0.65,
                    py: 0.2,
                    borderRadius: 999,
                    bgcolor: changeBg,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      color: changeColor,
                      fontWeight: 900,
                      fontSize: {
                        xs: '0.68rem',
                        sm: '0.76rem',
                      },
                      lineHeight: 1.15,
                    }}
                  >
                    {formattedChange}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: {
                      xs: '0.66rem',
                      sm: '0.76rem',
                    },
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}
                >
                  {changeLabel}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default PromotionKpiCard;